import { createSlice } from '@reduxjs/toolkit'

// ** Initial State
const initialState = {
  // Modal states
  modals: {
    userView: false,
    userEdit: false,
    transactionView: false,
    transactionEdit: false,
    confirmDelete: false,
    paymentMethod: false,
    otpVerification: false,
    giftCardSuccess: false,
    watchdogDecline: false
  },
  
  // Loading states
  loading: {
    global: false,
    users: false,
    transactions: false,
    payments: false,
    reports: false
  },
  
  // Notification states
  notifications: {
    show: false,
    type: 'info', // 'success', 'error', 'warning', 'info'
    message: '',
    duration: 3000
  },
  
  // Form states
  forms: {
    userForm: {
      isDirty: false,
      hasErrors: false,
      currentStep: 1,
      totalSteps: 3
    },
    transactionForm: {
      isDirty: false,
      hasErrors: false,
      currentStep: 1,
      totalSteps: 2
    }
  },
  
  // Table states
  tables: {
    users: {
      selectedRows: [],
      expandedRows: [],
      columnVisibility: {},
      columnOrder: [],
      columnWidths: {}
    },
    transactions: {
      selectedRows: [],
      expandedRows: [],
      columnVisibility: {},
      columnOrder: [],
      columnWidths: {}
    }
  },
  
  // Search and filter states
  search: {
    query: '',
    filters: {},
    sortBy: null,
    sortDirection: 'desc'
  },
  
  // Pagination states
  pagination: {
    users: {
      currentPage: 1,
      itemsPerPage: 10,
      totalItems: 0,
      totalPages: 0
    },
    transactions: {
      currentPage: 1,
      itemsPerPage: 10,
      totalItems: 0,
      totalPages: 0
    }
  },
  
  // Sidebar and navigation states
  sidebar: {
    collapsed: false,
    activeTab: 'dashboard',
    visibleTabs: ['dashboard', 'users', 'transactions', 'reports']
  },
  
  // Theme and display states
  theme: {
    darkMode: false,
    compactMode: false,
    fontSize: 'medium', // 'small', 'medium', 'large'
    colorScheme: 'default' // 'default', 'high-contrast', 'colorblind-friendly'
  },
  
  // Error states
  errors: {
    global: null,
    forms: {},
    api: {}
  },
  
  // Success states
  success: {
    global: null,
    forms: {},
    actions: {}
  }
}

// ** Slice
const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    // Modal actions
    openModal: (state, action) => {
      const { modalName } = action.payload
      state.modals[modalName] = true
    },
    
    closeModal: (state, action) => {
      const { modalName } = action.payload
      state.modals[modalName] = false
    },
    
    closeAllModals: (state) => {
      Object.keys(state.modals).forEach(modal => {
        state.modals[modal] = false
      })
    },
    
    // Loading actions
    setLoading: (state, action) => {
      const { key, value } = action.payload
      state.loading[key] = value
    },
    
    setGlobalLoading: (state, action) => {
      state.loading.global = action.payload
    },
    
    // Notification actions
    showNotification: (state, action) => {
      const { type, message, duration } = action.payload
      state.notifications = {
        show: true,
        type: type || 'info',
        message,
        duration: duration || 3000
      }
    },
    
    hideNotification: (state) => {
      state.notifications.show = false
    },
    
    // Form actions
    setFormState: (state, action) => {
      const { formName, updates } = action.payload
      state.forms[formName] = { ...state.forms[formName], ...updates }
    },
    
    resetFormState: (state, action) => {
      const { formName } = action.payload
      state.forms[formName] = initialState.forms[formName]
    },
    
    // Table actions
    setTableState: (state, action) => {
      const { tableName, updates } = action.payload
      state.tables[tableName] = { ...state.tables[tableName], ...updates }
    },
    
    selectTableRow: (state, action) => {
      const { tableName, rowId, selected } = action.payload
      if (selected) {
        state.tables[tableName].selectedRows.push(rowId)
      } else {
        state.tables[tableName].selectedRows = state.tables[tableName].selectedRows.filter(
          id => id !== rowId
        )
      }
    },
    
    clearTableSelection: (state, action) => {
      const { tableName } = action.payload
      state.tables[tableName].selectedRows = []
    },
    
    // Search and filter actions
    setSearchQuery: (state, action) => {
      state.search.query = action.payload
    },
    
    setSearchFilters: (state, action) => {
      state.search.filters = { ...state.search.filters, ...action.payload }
    },
    
    clearSearchFilters: (state) => {
      state.search.filters = {}
    },
    
    setSorting: (state, action) => {
      const { sortBy, sortDirection } = action.payload
      state.search.sortBy = sortBy
      state.search.sortDirection = sortDirection
    },
    
    // Pagination actions
    setPagination: (state, action) => {
      const { entity, updates } = action.payload
      state.pagination[entity] = { ...state.pagination[entity], ...updates }
    },
    
    // Sidebar actions
    toggleSidebar: (state) => {
      state.sidebar.collapsed = !state.sidebar.collapsed
    },
    
    setActiveTab: (state, action) => {
      state.sidebar.activeTab = action.payload
    },
    
    // Theme actions
    toggleDarkMode: (state) => {
      state.theme.darkMode = !state.theme.darkMode
    },
    
    setTheme: (state, action) => {
      state.theme = { ...state.theme, ...action.payload }
    },
    
    // Error actions
    setError: (state, action) => {
      const { key, error } = action.payload
      state.errors[key] = error
    },
    
    clearError: (state, action) => {
      const { key } = action.payload
      if (key) {
        delete state.errors[key]
      } else {
        state.errors = {}
      }
    },
    
    // Success actions
    setSuccess: (state, action) => {
      const { key, message } = action.payload
      state.success[key] = message
    },
    
    clearSuccess: (state, action) => {
      const { key } = action.payload
      if (key) {
        delete state.success[key]
      } else {
        state.success = {}
      }
    },
    
    // Reset actions
    resetUIState: () => initialState,
    resetModalStates: (state) => {
      state.modals = initialState.modals
    },
    resetLoadingStates: (state) => {
      state.loading = initialState.loading
    },
    resetFormStates: (state) => {
      state.forms = initialState.forms
    },
    resetTableStates: (state) => {
      state.tables = initialState.tables
    }
  }
})

export const {
  // Modal actions
  openModal,
  closeModal,
  closeAllModals,
  
  // Loading actions
  setLoading,
  setGlobalLoading,
  
  // Notification actions
  showNotification,
  hideNotification,
  
  // Form actions
  setFormState,
  resetFormState,
  
  // Table actions
  setTableState,
  selectTableRow,
  clearTableSelection,
  
  // Search and filter actions
  setSearchQuery,
  setSearchFilters,
  clearSearchFilters,
  setSorting,
  
  // Pagination actions
  setPagination,
  
  // Sidebar actions
  toggleSidebar,
  setActiveTab,
  
  // Theme actions
  toggleDarkMode,
  setTheme,
  
  // Error actions
  setError,
  clearError,
  
  // Success actions
  setSuccess,
  clearSuccess,
  
  // Reset actions
  resetUIState,
  resetModalStates,
  resetLoadingStates,
  resetFormStates,
  resetTableStates
} = uiSlice.actions

export default uiSlice.reducer
